/**
 * Utility functions for sanitizing and generating email-client-compatible HTML
 */

/**
 * Transforms raw editor HTML into email-friendly, inline-styled HTML.
 * Converts flex/grid containers to inline-styled table wrappers and
 * ensures all <img> tags have responsive max-width rules.
 */
export function sanitizeAndInlineEmailHtml(html: string): string {
  if (!html) return '';

  let clean = html;

  // 1. Ensure all <img> elements have responsive styles
  clean = clean.replace(/<img\s+([^>]*?)>/gi, (match, attrs) => {
    if (/style=["']/i.test(attrs)) {
      // Append max-width:100% !important; height:auto; if missing
      return match.replace(/style=["']([^"']*)["']/i, (_m, styleVal) => {
        let updated = styleVal;
        if (!/max-width/i.test(updated)) updated += '; max-width: 100% !important;';
        if (!/height/i.test(updated)) updated += '; height: auto !important;';
        return `style="${updated}"`;
      });
    } else {
      return `<img ${attrs} style="max-width: 100% !important; height: auto !important; display: block;" />`;
    }
  });

  // 2. Convert flex/grid containers to table wrappers
  clean = clean.replace(/<div\s+([^>]*?style=["'][^"']*(?:display:\s*(?:flex|grid))[^"']*["'][^>]*?)>([\s\S]*?)<\/div>/gi, (_match, _divAttrs, innerContent) => {
    return `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="width: 100%; border-collapse: collapse; margin: 8px 0;"><tr><td style="padding: 0;">${innerContent}</td></tr></table>`;
  });

  // 3. Ensure tables have email-compatible attributes
  clean = clean.replace(/<table(?!\s+border)([^>]*?)>/gi, '<table border="0" cellpadding="0" cellspacing="0" $1>');

  return clean;
}

/**
 * Validates image URLs for safety and CDN origins (e.g. Cloudinary, Firebase Storage)
 */
export function isValidEmailImageUrl(url: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}
